import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * iter875 - Centralised data layer for the /banks page.
 *
 * BEFORE: MyBanksPage + 4 sibling tile/banner components each issued their
 * own fetches for the same config/connections endpoints. With React 18
 * StrictMode dev-mode double-invoke that meant up to ×6 duplicate requests
 * per page load (per testing_agent iter874).
 *
 * NOW: this Context fetches ONCE per mount and deduplicates concurrent
 * fetches via an in-flight Promise map. Children consume read-only state
 * + a single `refresh()` action.
 */

const DEFAULT_STATE = {
  loading: true,
  bankAccounts: [],
  monoConfig: { enabled: false },
  monoConnections: [],
  plaidConfig: { enabled: false },
  plaidConnections: [],
  stitchConfig: { enabled: false },
  stitchConnections: [],
};

const BankFeedContext = createContext({
  ...DEFAULT_STATE,
  refresh: async () => {},
  refreshConnections: async () => {},
});

// In-flight request dedup + short-lived result cache.
//
// Two complementary mechanisms:
//
// 1. ``_inflight`` - concurrent calls for the same URL share one Promise.
//    Evicted INFLIGHT_TTL_MS after the request settles. Eliminates
//    duplicates from React 18 StrictMode dev-mode double-invocations
//    and rapid consecutive useEffect re-runs.
//
// 2. ``_resultCache`` - resolved results live for RESULT_TTL_MS, so even
//    if a component unmounts/remounts mid-request the result is reused
//    without a fresh network call. force=true on refresh() drops both.
const _inflight = new Map();
const _resultCache = new Map();
const INFLIGHT_TTL_MS = 5000;
const RESULT_TTL_MS = 30_000;

const _cachedGet = (url) => {
  const cached = _resultCache.get(url);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.value);
  }
  if (_inflight.has(url)) {
    return _inflight.get(url);
  }
  const promise = axios
    .get(url)
    .then((res) => {
      _resultCache.set(url, { value: res, expiresAt: Date.now() + RESULT_TTL_MS });
      return res;
    })
    .catch(() => ({ data: null }))
    .finally(() => {
      setTimeout(() => _inflight.delete(url), INFLIGHT_TTL_MS);
    });
  _inflight.set(url, promise);
  return promise;
};

const _invalidate = (urls) => {
  urls.forEach((u) => {
    _inflight.delete(u);
    _resultCache.delete(u);
  });
};

export const BankFeedProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULT_STATE);
  // Guard against StrictMode double-mount: only the first effect run wins
  // for the initial fetch.
  const initialFetchStarted = useRef(false);

  const fetchAll = useCallback(async ({ silent = false, bustCache = false } = {}) => {
    if (!silent) setState((prev) => ({ ...prev, loading: true }));
    // Only nuke the result cache when the caller explicitly opts in
    // (e.g., user clicks a "Force refresh" affordance). Default behaviour
    // is to trust the result-cache TTL - this dedupes the bursts of
    // refresh() that come from event handlers firing in rapid succession.
    if (bustCache) {
      _invalidate([
        `${API}/bank-accounts`,
        `${API}/mono/config`,
        `${API}/mono/connections`,
        `${API}/plaid/config`,
        `${API}/plaid/connections`,
        `${API}/stitch/config`,
        `${API}/stitch/connections`,
      ]);
    }
    try {
      const [bankRes, monoCfgRes, monoConnRes, plaidCfgRes, plaidConnRes, stitchCfgRes, stitchConnRes] =
        await Promise.all([
          _cachedGet(`${API}/bank-accounts`),
          _cachedGet(`${API}/mono/config`),
          _cachedGet(`${API}/mono/connections`),
          _cachedGet(`${API}/plaid/config`),
          _cachedGet(`${API}/plaid/connections`),
          _cachedGet(`${API}/stitch/config`),
          _cachedGet(`${API}/stitch/connections`),
        ]);
      setState({
        loading: false,
        bankAccounts: bankRes.data || [],
        monoConfig: monoCfgRes.data || { enabled: false },
        monoConnections: monoConnRes.data || [],
        plaidConfig: plaidCfgRes.data || { enabled: false },
        plaidConnections: plaidConnRes.data || [],
        stitchConfig: stitchCfgRes.data || { enabled: false },
        stitchConnections: stitchConnRes.data || [],
      });
    } catch (e) {
      // Each individual call already swallows its error via _cachedGet;
      // this catch is defensive against Promise.all itself failing.
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const refreshConnections = useCallback(async () => {
    try {
      const [bankRes, monoConnRes, plaidConnRes, stitchConnRes] = await Promise.all([
        _cachedGet(`${API}/bank-accounts`),
        _cachedGet(`${API}/mono/connections`),
        _cachedGet(`${API}/plaid/connections`),
        _cachedGet(`${API}/stitch/connections`),
      ]);
      setState((prev) => ({
        ...prev,
        bankAccounts: bankRes.data || prev.bankAccounts,
        monoConnections: monoConnRes.data || prev.monoConnections,
        plaidConnections: plaidConnRes.data || prev.plaidConnections,
        stitchConnections: stitchConnRes.data || prev.stitchConnections,
      }));
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    if (initialFetchStarted.current) return;
    initialFetchStarted.current = true;
    fetchAll();
  }, [fetchAll]);

  return (
    <BankFeedContext.Provider value={{
      ...state,
      refresh: fetchAll,
      hardRefresh: (opts = {}) => fetchAll({ bustCache: true, ...opts }),
      refreshConnections,
    }}>
      {children}
    </BankFeedContext.Provider>
  );
};

export const useBankFeedData = () => useContext(BankFeedContext);

export default BankFeedContext;
